import React, { useState } from 'react';
import { MosquePhoto } from '../types/mosque';
import { useLanguage } from '../contexts/LanguageContext';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import '../styles/PhotoGallery.css';

interface PhotoGalleryProps {
  photos: MosquePhoto[];
  loading?: boolean;
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({ photos, loading = false }) => {
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const openPhoto = (index: number) => setActiveIndex(index);
  const closePhoto = () => setActiveIndex(null);

  const showPrev = () => {
    if (activeIndex === null) return;
    setActiveIndex((activeIndex - 1 + photos.length) % photos.length);
  };

  const showNext = () => {
    if (activeIndex === null) return;
    setActiveIndex((activeIndex + 1) % photos.length);
  };

  if (loading) {
    return <p className="photo-gallery-empty">{t('photos.loading')}</p>;
  }

  if (!photos.length) {
    return <p className="photo-gallery-empty">{t('photos.noPhotos')}</p>;
  }

  return (
    <>
      <div className="photo-gallery-grid">
        {photos.map((photo, index) => (
          <button
            type="button"
            key={photo.id}
            className="photo-gallery-item"
            onClick={() => openPhoto(index)}
          >
            <img src={photo.url} alt={photo.caption || photo.fileName} />
          </button>
        ))}
      </div>

      {activeIndex !== null && (
        <div className="photo-lightbox" onClick={closePhoto}>
          <button
            type="button"
            className="photo-lightbox-close"
            onClick={closePhoto}
            aria-label={t('common.close')}
          >
            <X size={18} />
          </button>

          {photos.length > 1 && (
            <button
              type="button"
              className="photo-lightbox-nav prev"
              onClick={(event) => {
                event.stopPropagation();
                showPrev();
              }}
              aria-label={t('photos.previous')}
            >
              <ChevronLeft size={18} />
            </button>
          )}

          <div className="photo-lightbox-image-wrap" onClick={(event) => event.stopPropagation()}>
            <img src={photos[activeIndex].url} alt={photos[activeIndex].caption || photos[activeIndex].fileName} />
            {photos[activeIndex].caption && (
              <p className="photo-lightbox-caption">{photos[activeIndex].caption}</p>
            )}
          </div>

          {photos.length > 1 && (
            <button
              type="button"
              className="photo-lightbox-nav next"
              onClick={(event) => {
                event.stopPropagation();
                showNext();
              }}
              aria-label={t('photos.next')}
            >
              <ChevronRight size={18} />
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default PhotoGallery;
